import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import type { OrgOption, TaxComputation } from '../../types'
import { Alert, Card, CardBody, CardHeader, EmptyState, Select, Skeleton } from '../../components/ui'
import { CoinIcon } from '../../components/icons'

interface PayrollResponse { ok: boolean; org_id: string; orgs: OrgOption[]; period: string; periods: string[]; doc: TaxComputation | null }

export default function Payroll() {
  const [org, setOrg] = useState('')
  const [period, setPeriod] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'hr', 'payroll', org, period],
    queryFn: () => api.get<PayrollResponse>(
      `/ops/hr/payroll?${[org && `org=${org}`, period && `period=${period}`].filter(Boolean).join('&')}`,
    ),
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Payroll</h1>
          <p className="page-head__sub">Gross, PAYE, NAPSA, NHIMA and net pay per staff member.</p>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading || !data ? (
        <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
      ) : (
        <>
          <div className="row" style={{ marginBottom: 'var(--sp-5)', gap: 'var(--sp-4)', alignItems: 'flex-end' }}>
            {data.orgs.length > 1 && (
              <Select label="Cooperative" value={org || data.org_id} onChange={(e) => setOrg(e.target.value)} style={{ maxWidth: 260 }}>
                {data.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Select>
            )}
            {data.periods.length > 0 && (
              <Select label="Period" value={period || data.period} onChange={(e) => setPeriod(e.target.value)} style={{ maxWidth: 160 }}>
                {data.periods.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            )}
          </div>

          <Card>
            <CardHeader title="Payroll" sub={data.doc ? `${data.doc.org_name} · ${data.doc.period} · ${data.doc.headcount ?? 0} staff` : undefined} />
            <CardBody flush>
              {!data.doc || !data.doc.paye?.lines?.length ? (
                <EmptyState icon={<CoinIcon size={22} />} title="No payroll data"
                            body="Add salary amounts on staff records to see payroll here." />
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Name</th><th className="table__num">Gross</th><th className="table__num">PAYE</th>
                          <th className="table__num">NAPSA</th><th className="table__num">NHIMA</th>
                          <th className="table__num">Net pay</th></tr>
                    </thead>
                    <tbody>
                      {data.doc.paye.lines.map((l) => (
                        <tr key={l.uid}>
                          <td style={{ fontWeight: 600 }}>{l.name}</td>
                          <td className="table__num">{formatNumber(l.gross, 2)}</td>
                          <td className="table__num">{formatNumber(l.paye, 2)}</td>
                          <td className="table__num">{formatNumber(l.napsa, 2)}</td>
                          <td className="table__num">{formatNumber(l.nhima, 2)}</td>
                          <td className="table__num">{formatNumber(l.net_pay, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  )
}
